import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import { Avatar } from '../ui/avatar';
import {
  Mail,
  Calendar,
  Clock,
  MapPin,
  Users,
  AlertCircle,
  Reply,
  Forward,
  ExternalLink,
  ChevronDown,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { format, parseISO, formatDistance } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
// Si vous décidez de ne pas utiliser DOMPurify, commentez cette ligne
// import DOMPurify from 'dompurify';
import axiosInstance from '@/lib/api/axiosInstance';

// Types for our objects
interface EmailContent {
  id: string;
  threadId: string;
  payload?: {
    headers?: { name: string; value: string }[];
    parts?: any[];
    body?: { data?: string; size?: number };
    mimeType?: string;
  };
  snippet?: string;
  labelIds?: string[];
  internalDate?: string;
}

interface ThreadContent {
  id: string;
  messages: EmailContent[];
}

interface EventDetails {
  id: string;
  summary: string;
  description?: string;
  location?: string;
  start: { dateTime: string; timeZone?: string };
  end: { dateTime: string; timeZone?: string };
  attendees?: { email: string; displayName?: string; responseStatus?: string }[];
  organizer?: { email: string; displayName?: string };
  htmlLink?: string;
  attachments?: { fileUrl: string; title: string; mimeType: string }[];
  colorId?: string;
}

interface ObjectDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  objectType: 'email' | 'event';
  objectId: string;
  threadId?: string;
  calendarId?: string;
}

// Nouveau composant pour afficher un message individuel
const EmailMessageItem = ({ 
  message, 
  index,
  totalMessages, 
  locale, 
  findEmailBodyContent
}: { 
  message: EmailContent, 
  index: number,
  totalMessages: number,
  locale: Locale,
  findEmailBodyContent: (email: EmailContent) => string
}) => {
  const [isExpanded, setIsExpanded] = useState(index === 0); // Premier message ouvert par défaut (qui est le plus récent)
  
  const { from, date } = extractEmailInfo(message);
  const senderMatch = from.match(/([^<]+)\s*(?:<[^>]*>)?/);
  const senderName = senderMatch && senderMatch[1] ? senderMatch[1].trim() : from;
  
  // Correction de l'erreur TypeScript avec une meilleure gestion des expressions régulières
  const emailMatch = from.match(/<([^>]*)>/);
  const senderEmail = emailMatch && emailMatch[1] ? emailMatch[1] : from;
  
  // Try to get initials for avatar
  const initials = senderName
    .split(' ')
    .map(name => name[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
  
  const formattedDate = date ? format(new Date(date), 'Pp', { locale }) : '';
  
  return (
    <div key={message.id} className="border rounded-md overflow-hidden">
      <div 
        className="bg-muted/30 p-4 flex items-center space-x-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Avatar>
          <div className="flex h-full w-full items-center justify-center bg-primary/10 text-primary font-semibold">
            {initials}
          </div>
        </Avatar>
        <div className="flex-1">
          <div className="font-medium">{senderName}</div>
          <div className="text-sm text-muted-foreground">{senderEmail}</div>
          <div className="text-xs text-muted-foreground">{formattedDate}</div>
        </div>
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <span className="text-xs opacity-50">#{index+1}/{totalMessages}</span>
          <ChevronDown 
            className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
          />
        </div>
      </div>
      
      {isExpanded && (
        <div 
          className="p-4 prose prose-sm max-w-none dark:prose-invert border-t" 
          dangerouslySetInnerHTML={{ __html: findEmailBodyContent(message) }} 
        />
      )}
    </div>
  );
}

// Helper function to decode base64 email body
const decodeEmailBody = (data: string): string => {
  try {
    // Replace URL safe characters and add padding if needed
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/');
    const decodedContent = atob(base64);
    
    // Convertir le contenu décodé en UTF-8 pour gérer correctement les caractères spéciaux
    // Cette étape est cruciale pour éviter les problèmes d'affichage comme "receiveÂ", "Apologies â", etc.
    const bytes = new Uint8Array(decodedContent.length);
    for (let i = 0; i < decodedContent.length; i++) {
      bytes[i] = decodedContent.charCodeAt(i);
    }
    const decoder = new TextDecoder('utf-8');
    let cleanContent = decoder.decode(bytes);
    
    // Nettoyer les caractères non-imprimables et les remplacements incorrects
    cleanContent = cleanContent
      .replace(/Â/g, '') // Supprimer les Â incorrects
      .replace(/â\s+/g, '— ') // Remplacer les tirets mal codés
      .replace(/\r\n/g, '\n') // Normaliser les sauts de ligne
      .replace(/\n{3,}/g, '\n\n'); // Éviter trop de sauts de ligne consécutifs
    
    // Sanitize HTML content
    // Si vous utilisez DOMPurify:
    // return DOMPurify.sanitize(cleanContent);
    
    // Alternative sans DOMPurify (moins sécurisée):
    return cleanContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
  } catch (error) {
    console.error('Error decoding email body:', error);
    return 'Impossible de décoder le contenu de l\'email.';
  }
};

// Helper function to extract email info (moved outside of the component)
const extractEmailInfo = (email: EmailContent) => {
  if (!email?.payload?.headers) return { subject: '', from: '', to: '', date: '' };
  
  const subject = email.payload.headers.find(h => h.name === 'Subject')?.value || '';
  const from = email.payload.headers.find(h => h.name === 'From')?.value || '';
  const to = email.payload.headers.find(h => h.name === 'To')?.value || '';
  const date = email.payload.headers.find(h => h.name === 'Date')?.value || '';
  
  return { subject, from, to, date };
};

export const ObjectDrawer: React.FC<ObjectDrawerProps> = ({
  isOpen,
  onClose,
  objectType,
  objectId,
  threadId,
  calendarId = 'primary'
}) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'fr' ? fr : enUS;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailContent, setEmailContent] = useState<EmailContent | null>(null);
  const [threadContent, setThreadContent] = useState<ThreadContent | null>(null);
  const [eventDetails, setEventDetails] = useState<EventDetails | null>(null);

  useEffect(() => {
    if (isOpen && objectId) {
      fetchObjectContent();
    }
  }, [isOpen, objectId, objectType, threadId, calendarId]);

  const fetchObjectContent = async () => {
    setLoading(true);
    setError(null);

    try {
      if (objectType === 'email') {
        // If threadId is provided and it's not the same as objectId, fetch the entire thread
        const fullThread = threadId && threadId !== objectId;
        
        // Corriger les routes pour correspondre exactement à celles définies dans le backend
        // Note: Problème de routes détecté - log supplémentaire pour aider au debug
        let endpoint;
        if (fullThread) {
          endpoint = `/google/gmail/threads/${threadId}`;
          console.log(`Using thread endpoint: ${endpoint} with fullThread=${fullThread}`);
        } else {
          endpoint = `/google/gmail/emails/${objectId}`;
          console.log(`Using email endpoint: ${endpoint}`);
        }
        
        // Ajouter le paramètre fullThread dans l'URL au lieu de l'URL elle-même
        const params = fullThread ? { fullThread: 'true' } : undefined;
        
        console.log(`Fetching email content from: ${endpoint}`, params);
        
        if (fullThread) {
          const response = await axiosInstance.get<ThreadContent>(endpoint, { params });
          // Log de la réponse pour aider au debug
          console.log('Thread content response:', response.status, response.statusText);
          setThreadContent(response.data);
        } else {
          const response = await axiosInstance.get<EmailContent>(endpoint);
          // Log de la réponse pour aider au debug
          console.log('Email content response:', response.status, response.statusText);
          setEmailContent(response.data);
        }
      } else if (objectType === 'event') {
        // Corriger l'URL pour correspondre à vos routes backend
        let endpoint;
        if (calendarId) {
          endpoint = `/google/calendar/${calendarId}/events/${objectId}`;
        } else {
          endpoint = `/google/calendar/events/${objectId}/details`;
        }
        
        console.log(`Fetching event details from: ${endpoint}`);
        const response = await axiosInstance.get<EventDetails>(endpoint);
        console.log('Event details response:', response.status, response.statusText);
        setEventDetails(response.data);
      }
    } catch (err: any) {
      // Améliorer le logging d'erreur
      console.error(`Error fetching ${objectType} content:`, err);
      console.error('Request details:', {
        url: err.config?.url,
        method: err.config?.method,
        params: err.config?.params,
        responseUrl: err.request?.responseURL,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data
      });
      setError(t(`${objectType}.fetchError`, `Impossible de récupérer le contenu de ${objectType === 'email' ? 'l\'email' : 'l\'événement'}`));
    } finally {
      setLoading(false);
    }
  };

  // Find the body content of the email
  const findEmailBodyContent = (email: EmailContent): string => {
    if (!email || !email.payload) return '';
    
    // Helper to find HTML or text content in parts recursively
    const findContentInParts = (parts: any[]): string => {
      // First try to find HTML content
      for (const part of parts) {
        if (part.mimeType === 'text/html' && part.body?.data) {
          return decodeEmailBody(part.body.data);
        }
        
        // Check nested parts if present
        if (part.parts && part.parts.length) {
          const nestedContent = findContentInParts(part.parts);
          if (nestedContent) return nestedContent;
        }
      }
      
      // If no HTML found, try to find text content
      for (const part of parts) {
        if (part.mimeType === 'text/plain' && part.body?.data) {
          const textContent = decodeEmailBody(part.body.data);
          return `<pre style="white-space: pre-wrap; font-family: inherit;">${textContent}</pre>`;
        }
      }
      
      return '';
    };
    
    // If email has parts, search in them
    if (email.payload.parts && email.payload.parts.length) {
      return findContentInParts(email.payload.parts);
    }
    
    // If email has a direct body
    if (email.payload.body?.data) {
      if (email.payload.mimeType === 'text/html') {
        return decodeEmailBody(email.payload.body.data);
      } else if (email.payload.mimeType === 'text/plain') {
        const textContent = decodeEmailBody(email.payload.body.data);
        return `<pre style="white-space: pre-wrap; font-family: inherit;">${textContent}</pre>`;
      }
    }
    
    // If snippet is available as fallback
    if (email.snippet) {
      return `<p>${email.snippet}</p>`;
    }
    
    return '<p>Aucun contenu disponible</p>';
  };

  // Render email content
  const renderEmailContent = () => {
    if (loading) {
      return (
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-3/4" />
          <div className="flex items-center space-x-2">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-6 text-center text-destructive">
          <AlertCircle className="mx-auto h-10 w-10 mb-2" />
          <p>{error}</p>
        </div>
      );
    }

    // Show thread view if available
    if (threadContent && threadContent.messages && threadContent.messages.length > 0) {
      // Trier les messages par date (du plus récent au plus ancien) - Ordre inversé
      const sortedMessages = [...threadContent.messages].sort((a, b) => {
        const dateA = a.internalDate ? parseInt(a.internalDate) : 0;
        const dateB = b.internalDate ? parseInt(b.internalDate) : 0;
        return dateB - dateA; // Trier du plus récent au plus ancien
      });

      return (
        <div className="space-y-6 p-4">
          {/* Thread subject as title - ajout du nombre de messages dans le sujet */}
          {sortedMessages.length > 0 && (
            <div className="flex justify-between items-center">
              <div className="text-xl font-bold">
                {extractEmailInfo(sortedMessages[0]).subject}
              </div>
              <Badge variant="secondary">
                {sortedMessages.length} {t('emails.messageCount', 'message(s)')}
              </Badge>
            </div>
          )}
          
          {/* Display each message in the thread as collapsible panels */}
          <div className="space-y-3">
            {sortedMessages.map((message, i) => (
              <EmailMessageItem 
                key={message.id}
                message={message} 
                index={i}
                totalMessages={sortedMessages.length}
                locale={locale}
                findEmailBodyContent={findEmailBodyContent}
              />
            ))}
          </div>
          
          {/* Quick reply box */}
          <div className="mt-4 border rounded-md p-3">
            <div className="text-sm text-muted-foreground mb-2">{t('emails.quickReply', 'Réponse rapide')}</div>
            <textarea 
              className="w-full p-2 min-h-[100px] border rounded-sm text-sm" 
              placeholder={t('emails.typeYourReply', 'Tapez votre réponse ici...')}
            />
            <div className="mt-2 flex justify-end">
              <Button size="sm">
                <Reply className="w-4 h-4 mr-2" />
                {t('emails.reply', 'Répondre')}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Single email view
    if (emailContent) {
      const { subject, from, to, date } = extractEmailInfo(emailContent);
      const formattedDate = date ? format(new Date(date), 'Pp', { locale }) : '';
      
      // Extract sender information
      const senderMatch = from.match(/([^<]+)\s*(?:<[^>]*>)?/);
      const senderName = senderMatch && senderMatch[1] ? senderMatch[1].trim() : from;
      
      // Correction de l'erreur TypeScript avec une meilleure gestion des expressions régulières
      const emailMatch = from.match(/<([^>]*)>/);
      const senderEmail = emailMatch && emailMatch[1] ? emailMatch[1] : from;
      
      // Try to get initials for avatar
      const initials = senderName
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
      
      return (
        <div className="p-4 space-y-4">
          <h2 className="text-xl font-bold">{subject}</h2>
          
          <div className="flex items-start space-x-3">
            <Avatar>
              <div className="flex h-full w-full items-center justify-center bg-primary/10 text-primary font-semibold">
                {initials}
              </div>
            </Avatar>
            <div className="flex-1">
              <div className="font-medium">{senderName}</div>
              <div className="text-sm text-muted-foreground">{senderEmail}</div>
              {to && (
                <div className="text-xs text-muted-foreground mt-1">
                  À: {to}
                </div>
              )}
              {formattedDate && (
                <div className="text-xs text-muted-foreground">
                  {formattedDate}
                </div>
              )}
            </div>
          </div>
          
          <div 
            className="mt-4 prose prose-sm max-w-none dark:prose-invert" 
            dangerouslySetInnerHTML={{ __html: findEmailBodyContent(emailContent) }} 
          />
        </div>
      );
    }

    return (
      <div className="p-6 text-center text-muted-foreground">
        <Mail className="mx-auto h-10 w-10 mb-2" />
        <p>{t('emails.noContent', 'Aucun contenu disponible')}</p>
      </div>
    );
  };

  // Render event content
  const renderEventContent = () => {
    if (loading) {
      return (
        <div className="space-y-4 p-6">
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-6 text-center text-destructive">
          <AlertCircle className="mx-auto h-10 w-10 mb-2" />
          <p>{error}</p>
        </div>
      );
    }

    if (eventDetails) {
      const { 
        summary, 
        description, 
        location, 
        start, 
        end, 
        attendees, 
        organizer
      } = eventDetails;

      // Format date and time
      const startDate = parseISO(start.dateTime);
      const endDate = parseISO(end.dateTime);
      const formattedDate = format(startDate, 'PPPP', { locale });
      const startTime = format(startDate, 'HH:mm', { locale });
      const endTime = format(endDate, 'HH:mm', { locale });
      const duration = formatDistance(startDate, endDate, { 
        locale,
        addSuffix: false 
      });

      // Sanitize event description if available
      // Si vous utilisez DOMPurify:
      // const sanitizedDescription = description ? DOMPurify.sanitize(description) : '';
      
      // Alternative sans DOMPurify (moins sécurisée):
      const sanitizedDescription = description ? description.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "") : '';
      
      return (
        <div className="p-4 space-y-6">
          <h2 className="text-xl font-bold">{summary}</h2>
          
          <div className="flex flex-col space-y-3">
            {/* Date & Time */}
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-3 text-muted-foreground flex-shrink-0" />
              <div>
                <div className="font-medium">{formattedDate}</div>
                <div className="text-sm text-muted-foreground">
                  {startTime} - {endTime} ({duration})
                </div>
              </div>
            </div>
            
            {/* Location */}
            {location && (
              <div className="flex items-center">
                <MapPin className="w-5 h-5 mr-3 text-muted-foreground flex-shrink-0" />
                <div className="text-sm">{location}</div>
              </div>
            )}
            
            {/* Organizer */}
            {organizer && (
              <div className="flex items-center">
                <Users className="w-5 h-5 mr-3 text-muted-foreground flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium">
                    {t('calendar.organizer', 'Organisateur')}:
                  </div>
                  <div className="text-sm">{organizer.displayName || organizer.email}</div>
                </div>
              </div>
            )}
            
            {/* Attendees */}
            {attendees && attendees.length > 0 && (
              <div className="flex items-start">
                <Users className="w-5 h-5 mr-3 text-muted-foreground flex-shrink-0 mt-1" />
                <div>
                  <div className="text-sm font-medium">
                    {t('calendar.attendees', 'Participants')} ({attendees.length}):
                  </div>
                  <ul className="text-sm space-y-1 mt-1">
                    {attendees.map((attendee, index) => (
                      <li key={index} className="flex items-center">
                        <Badge variant="outline" className="mr-2">
                          {attendee.responseStatus === 'accepted' ? '✓' : 
                           attendee.responseStatus === 'declined' ? '✗' : '?'}
                        </Badge>
                        {attendee.displayName || attendee.email}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
          
          {/* Description */}
          {sanitizedDescription && (
            <div className="mt-6">
              <h3 className="text-sm font-medium mb-2">
                {t('calendar.description', 'Description')}:
              </h3>
              <div 
                className="text-sm prose prose-sm max-w-none dark:prose-invert border-t pt-2"
                dangerouslySetInnerHTML={{ __html: sanitizedDescription }} 
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="p-6 text-center text-muted-foreground">
        <Calendar className="mx-auto h-10 w-10 mb-2" />
        <p>{t('calendar.noDetails', 'Aucun détail disponible')}</p>
      </div>
    );
  };

  // Determine drawer title and content based on object type
  const getDrawerTitle = () => {
    if (loading) return t('common.loading', 'Chargement...');
    
    if (objectType === 'email') {
      if (threadContent && threadContent.messages && threadContent.messages.length > 0) {
        return t('emails.conversation', 'Conversation');
      }
      return t('emails.email', 'Email');
    } else {
      return t('calendar.event', 'Événement');
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg lg:max-w-xl overflow-y-auto">
        <SheetHeader className="flex justify-between items-center">
          <div className="flex items-center">
            {objectType === 'email' ? (
              <Mail className="w-5 h-5 mr-2" />
            ) : (
              <Calendar className="w-5 h-5 mr-2" />
            )}
            <SheetTitle>{getDrawerTitle()}</SheetTitle>
          </div>
        </SheetHeader>
        
        {/* Main content area */}
        <div className="flex-1 overflow-auto my-6">
          {objectType === 'email' ? renderEmailContent() : renderEventContent()}
        </div>
        
        {/* Footer actions */}
        <SheetFooter className="flex flex-row justify-between mt-auto">
          {objectType === 'email' ? (
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Reply className="w-4 h-4 mr-2" />
                {t('emails.reply', 'Répondre')}
              </Button>
              <Button variant="outline" size="sm">
                <Forward className="w-4 h-4 mr-2" />
                {t('emails.forward', 'Transférer')}
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              {eventDetails?.htmlLink && (
                <Button variant="outline" size="sm" asChild>
                  <a href={eventDetails.htmlLink} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('calendar.openInGoogle', 'Ouvrir dans Google')}
                  </a>
                </Button>
              )}
            </div>
          )}
          <Button variant="outline" onClick={onClose}>
            {t('common.close', 'Fermer')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
